import * as Cesium from "cesium";
import {
    createContext,
    Dispatch,
    SetStateAction,
    useState,
    useMemo,
} from "react";
import {
    ModalsType,
    ModalStoreType,
    ScenarioLoaderType,
    ScenarioFormDataType,
    ScenarioBaseDataType,
    SelectedGroupSatelliteType,
    OrbitAnimationDetailsType,
} from "../utils/scenario-types";
import { ScenarioResponseType } from "../../types";
import { PopoverProvider } from "../../../CommonComponents/popover/PopoverProvider";
import { OrbitStartOptionsType } from "../../../CommonComponents/orbital-insights/orbital-insights-type";
import { DEFAULT_TIMELINE_FILTER, getInitialFormData, REPORT_WINDOWS, WINDOW_INITIAL_Z_INDEX } from "../utils/constants";
import useNavigation from "../hooks/useNavigation";
import { ThermalModelConfig } from "../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/ThermalConfig";
import { TimelineFilterStateType } from "../../../CommonComponents/timeline/TimelineFilter";

type ScenarioContextType = {
    viewer: Cesium.Viewer | null;
    setViewer: Dispatch<SetStateAction<Cesium.Viewer | null>>;
    socket: WebSocket | null;
    setSocket: Dispatch<SetStateAction<WebSocket | null>>;

    loading: ScenarioLoaderType;
    setLoading: Dispatch<SetStateAction<ScenarioLoaderType>>;
    satelliteBaseDataLoaded: boolean;
    setSatelliteBaseDataLoaded: Dispatch<SetStateAction<boolean>>;

    modals: ModalsType;
    setModals: Dispatch<SetStateAction<ModalsType>>;

    windowStore: ModalStoreType;
    setWindowStore: Dispatch<SetStateAction<ModalStoreType>>;

    scenarioResponse: ScenarioResponseType | null;
    setScenarioResponse: Dispatch<SetStateAction<ScenarioResponseType | null>>;

    formData: ScenarioFormDataType;
    setFormData: Dispatch<SetStateAction<ScenarioFormDataType>>;

    thermalHeatmapData: any;
    setThermalHeatmapData: Dispatch<SetStateAction<any>>;

    thermalEnvParams: { albedo_factor: number | null; blackbody_temperature: number | null; solar_constant: number | null } | null;
    setThermalEnvParams: Dispatch<SetStateAction<{ albedo_factor: number | null; blackbody_temperature: number | null; solar_constant: number | null } | null>>;

    thermalModel: ThermalModelConfig | null;
    setThermalModel: Dispatch<SetStateAction<ThermalModelConfig | null>>;

    isThermalOpen: boolean;
    setIsThermalOpen: Dispatch<SetStateAction<boolean>>;

    scenarioBaseData: ScenarioBaseDataType;
    setScenarioBaseData: Dispatch<SetStateAction<ScenarioBaseDataType>>;

    selectedGroupSatellite: SelectedGroupSatelliteType;
    setSelectedGroupSatellite: Dispatch<SetStateAction<SelectedGroupSatelliteType>>

    orbitAnimationDetails: OrbitAnimationDetailsType;
    setOrbitAnimationDetails: Dispatch<SetStateAction<OrbitAnimationDetailsType>>

    orbitStartOption: OrbitStartOptionsType;
    setOrbitStartOption: Dispatch<SetStateAction<OrbitStartOptionsType>>

    scenarioJson: any;
    setScenarioJson: Dispatch<SetStateAction<any>>;

    simulatorList: any[];
    setSimulatorList: Dispatch<SetStateAction<any[]>>;

    selectedSimulator: Record<string, string>;
    setSelectedSimulator: Dispatch<SetStateAction<Record<string, string>>>;

    timelineFilter: TimelineFilterStateType;
    setTimelineFilter: Dispatch<SetStateAction<TimelineFilterStateType>>;
};

export const ScenarioStatesProviderContext = createContext<ScenarioContextType | undefined>(undefined);


const StatesProvider = ({ children }: { children: React.ReactNode }) => {
    const INITIAL_FORM_DATA = getInitialFormData()
    const { type } = useNavigation()
    const [viewer, setViewer] = useState<Cesium.Viewer | null>(null);
    const [socket, setSocket] = useState<WebSocket | null>(null);
    const [scenarioResponse, setScenarioResponse] = useState<ScenarioResponseType | null>(null);
    const [scenarioJson, setScenarioJson] = useState<any>(null);
    const [simulatorList, setSimulatorList] = useState<any[]>([]);
    const [selectedSimulator, setSelectedSimulator] = useState<Record<string, string>>({});

    const [loading, setLoading] = useState<ScenarioLoaderType>({
        base: true,
        execution: false,
        executionMessage: false,
        undeploy: false
    });
    const [satelliteBaseDataLoaded, setSatelliteBaseDataLoaded] = useState(false);

    const [modals, setModals] = useState<ModalsType>({
        configured: null,
        dialouge: null
    });

    const [windowStore, setWindowStore] = useState<ModalStoreType>({
        nextZindex: WINDOW_INITIAL_Z_INDEX,
        modals: REPORT_WINDOWS,
    });

    const [scenarioBaseData, setScenarioBaseData] = useState<ScenarioBaseDataType>({
        allGroups: [],
        allStations: [],
        allFaults: {},
        allTaskPlans: {},
        allGeoFeatures: [],
        allPayloads: {},
        allSubsystems: {},
        allHwilComponents: {
            payload: [],
            bus: []
        }
    });

    const [formData, setFormData] = useState({
        ...INITIAL_FORM_DATA,
        details: {
            ...INITIAL_FORM_DATA.details,
            scenarioDeploymentType: type === 'simulation' ? 'SIMULATE' : 'TRUETWIN_COMMAND_CENTER'
        },
        segments: {
            ...INITIAL_FORM_DATA.segments,
            contactType: type === 'simulation' ? 'Continuous' : 'Pass-Based'
        }
    });
    const [orbitStartOption, setOrbitStartOption] = useState<OrbitStartOptionsType>('Sunlit');
    const [selectedGroupSatellite, setSelectedGroupSatellite] = useState<SelectedGroupSatelliteType>(null);
    const [thermalHeatmapData, setThermalHeatmapData] = useState<any>(null);
    const [isThermalOpen, setIsThermalOpen] = useState(false);
    const [thermalEnvParams, setThermalEnvParams] = useState<{ albedo_factor: number | null; blackbody_temperature: number | null; solar_constant: number | null } | null>(null);
    const [thermalModel, setThermalModel] = useState<ThermalModelConfig | null>(null);

    const [orbitAnimationDetails, setOrbitAnimationDetails] = useState<OrbitAnimationDetailsType>({
        startAnimation: false,
        animationSpeed: 2,
        controllerState: 'pause',
        showAnimationDetails: false,
        showAxisControl: true,
        expandedView: false
    });

    const [timelineFilter, setTimelineFilter] = useState<TimelineFilterStateType>(DEFAULT_TIMELINE_FILTER)

    /* ----------------- MEMOIZED CONTEXT VALUE ------------------ */
    const contextValue = useMemo<ScenarioContextType>(() => ({
        viewer,
        setViewer,
        socket,
        setSocket,
        loading,
        setLoading,
        satelliteBaseDataLoaded,
        setSatelliteBaseDataLoaded,
        modals,
        setModals,
        windowStore,
        setWindowStore,
        formData,
        setFormData,
        scenarioResponse,
        thermalHeatmapData,
        setThermalHeatmapData,
        thermalEnvParams,
        setThermalEnvParams,
        thermalModel,
        setThermalModel,
        isThermalOpen,
        setIsThermalOpen,
        setScenarioResponse,
        scenarioBaseData,
        setScenarioBaseData,
        selectedGroupSatellite,
        setSelectedGroupSatellite,
        orbitAnimationDetails,
        setOrbitAnimationDetails,
        orbitStartOption,
        setOrbitStartOption,
        scenarioJson,
        setScenarioJson,
        simulatorList,
        setSimulatorList,
        selectedSimulator,
        setSelectedSimulator,
        timelineFilter,
        setTimelineFilter
    }), [viewer, socket, loading, satelliteBaseDataLoaded, modals, selectedGroupSatellite, windowStore, scenarioResponse, formData, scenarioBaseData, orbitAnimationDetails, orbitStartOption, scenarioJson, simulatorList, selectedSimulator, isThermalOpen, thermalEnvParams, thermalModel, thermalHeatmapData, timelineFilter]);

    return (
        <PopoverProvider>
            <ScenarioStatesProviderContext.Provider value={contextValue}>
                {children}
            </ScenarioStatesProviderContext.Provider>
        </PopoverProvider>
    );
};

export default StatesProvider;
